import {
	FormatBold,
	FormatItalic,
	FormatListBulleted,
	FormatListNumbered,
	FormatQuote,
	Image,
	Link,
	OndemandVideo,
	Title,
} from "@mui/icons-material";
import { Box, CircularProgress, IconButton, Paper, Stack, Tooltip } from "@mui/material";
import {
	type ChangeEvent,
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { toast } from "react-toastify";
import type { FeedbackUploadInput } from "../../Feedback/feedbackApi.ts";
import { useUiI18n } from "../../I18n";

interface FeedbackRichEditorProps {
	value: string;
	onChange: (value: string) => void;
	onUpload: (upload: FeedbackUploadInput) => void;
	onMediaBusyChange?: (busy: boolean) => void;
}

export interface FeedbackRichEditorHandle {
	getHtml: () => string;
	getText: () => string;
}

const readFile = async (file: File): Promise<string> =>
	await new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
		reader.onerror = () => reject(reader.error ?? new Error("Unable to read file"));
		reader.readAsDataURL(file);
	});

const escapeAttribute = (value: string): string =>
	value.split("&").join("&amp;").split('"').join("&quot;").split("<").join("&lt;");

export const FeedbackRichEditor = forwardRef<FeedbackRichEditorHandle, FeedbackRichEditorProps>(
	({ value, onChange, onUpload, onMediaBusyChange }, forwardedRef) => {
		const { t } = useUiI18n();
		const editorRef = useRef<HTMLDivElement | null>(null);
		const imageInputRef = useRef<HTMLInputElement | null>(null);
		const videoInputRef = useRef<HTMLInputElement | null>(null);
		const objectUrlsRef = useRef<string[]>([]);
		const selectionRef = useRef<Range | null>(null);
		const [mediaBusy, setMediaBusy] = useState(false);

		useImperativeHandle(
			forwardedRef,
			() => ({
				getHtml: () => editorRef.current?.innerHTML ?? value,
				getText: () => editorRef.current?.innerText ?? editorRef.current?.textContent ?? "",
			}),
			[value],
		);

		useEffect(
			() => () => {
				for (const url of objectUrlsRef.current) URL.revokeObjectURL(url);
			},
			[],
		);

		useEffect(() => {
			if (
				editorRef.current &&
				editorRef.current.innerHTML !== value &&
				!editorRef.current.matches(":focus")
			) {
				editorRef.current.innerHTML = value;
			}
		}, [value]);

		const run = (command: string, commandValue?: string): void => {
			editorRef.current?.focus();
			document.execCommand(command, false, commandValue);
			onChange(editorRef.current?.innerHTML ?? "");
		};

		const rememberSelection = (): void => {
			const selection = window.getSelection();
			const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
			selectionRef.current =
				range && editorRef.current?.contains(range.commonAncestorContainer)
					? range.cloneRange()
					: null;
		};

		const insertMedia = (html: string): void => {
			const editor = editorRef.current;
			if (!editor) return;
			editor.focus();
			const selection = window.getSelection();
			if (selection && selectionRef.current) {
				selection.removeAllRanges();
				selection.addRange(selectionRef.current);
				document.execCommand("insertHTML", false, html);
			} else {
				editor.insertAdjacentHTML("beforeend", html);
			}
			selectionRef.current = null;
			onChange(editor.innerHTML);
		};

		const addLink = (): void => {
			const href = window.prompt(t("feedback.editor.linkPrompt"), "https://");
			if (href?.trim()) run("createLink", href.trim());
		};

		const addMedia = async (event: ChangeEvent<HTMLInputElement>, kind: "image" | "video") => {
			const file = event.target.files?.[0];
			event.target.value = "";
			if (!file) return;
			setMediaBusy(true);
			onMediaBusyChange?.(true);
			try {
				const id = crypto.randomUUID();
				const dataUrl = await readFile(file);
				const objectUrl = URL.createObjectURL(file);
				objectUrlsRef.current.push(objectUrl);
				onUpload({ id, name: file.name, kind, dataUrl });
				const name = escapeAttribute(file.name);
				const html =
					kind === "image"
						? `<img src="${objectUrl}" data-feedback-upload-id="${id}" alt="${name}">`
						: `<video src="${objectUrl}" data-feedback-upload-id="${id}" controls preload="metadata"></video>`;
				insertMedia(html);
			} catch {
				toast.error(t("feedback.editor.uploadFailed"));
			} finally {
				setMediaBusy(false);
				onMediaBusyChange?.(false);
			}
		};

		const tools = [
			{ label: t("feedback.editor.bold"), icon: <FormatBold />, action: () => run("bold") },
			{ label: t("feedback.editor.italic"), icon: <FormatItalic />, action: () => run("italic") },
			{
				label: t("feedback.editor.heading"),
				icon: <Title />,
				action: () => run("formatBlock", "h2"),
			},
			{
				label: t("feedback.editor.bullets"),
				icon: <FormatListBulleted />,
				action: () => run("insertUnorderedList"),
			},
			{
				label: t("feedback.editor.numbered"),
				icon: <FormatListNumbered />,
				action: () => run("insertOrderedList"),
			},
			{
				label: t("feedback.editor.quote"),
				icon: <FormatQuote />,
				action: () => run("formatBlock", "blockquote"),
			},
			{ label: t("feedback.editor.link"), icon: <Link />, action: addLink },
		];

		return (
			<Paper className="feedback-composer__editor" variant="outlined">
				<Stack className="feedback-composer__toolbar" direction="row" flexWrap="wrap">
					{tools.map((tool) => (
						<Tooltip key={tool.label} title={tool.label}>
							<IconButton size="small" aria-label={tool.label} onClick={tool.action}>
								{tool.icon}
							</IconButton>
						</Tooltip>
					))}
					<Tooltip title={t("feedback.editor.image")}>
						<IconButton
							size="small"
							aria-label={t("feedback.editor.image")}
							disabled={mediaBusy}
							onMouseDown={(event) => {
								event.preventDefault();
								rememberSelection();
							}}
							onClick={() => imageInputRef.current?.click()}
						>
							{mediaBusy ? <CircularProgress size={20} /> : <Image />}
						</IconButton>
					</Tooltip>
					<Tooltip title={t("feedback.editor.video")}>
						<IconButton
							size="small"
							aria-label={t("feedback.editor.video")}
							disabled={mediaBusy}
							onMouseDown={(event) => {
								event.preventDefault();
								rememberSelection();
							}}
							onClick={() => videoInputRef.current?.click()}
						>
							<OndemandVideo />
						</IconButton>
					</Tooltip>
					<input
						ref={imageInputRef}
						hidden
						type="file"
						accept="image/png,image/jpeg,image/webp,image/gif"
						onChange={(event) => void addMedia(event, "image")}
					/>
					<input
						ref={videoInputRef}
						hidden
						type="file"
						accept="video/mp4,video/webm,video/quicktime"
						onChange={(event) => void addMedia(event, "video")}
					/>
				</Stack>
				<Box
					className="feedback-composer__writing-area"
					ref={editorRef}
					contentEditable
					suppressContentEditableWarning
					role="textbox"
					aria-multiline="true"
					aria-label={t("feedback.description")}
					data-placeholder={t("feedback.descriptionPlaceholder")}
					onInput={(event) => onChange(event.currentTarget.innerHTML)}
				/>
			</Paper>
		);
	},
);

FeedbackRichEditor.displayName = "FeedbackRichEditor";
